// src/app/core/sentry/sentry.replay-filters.ts

export interface ReplayFilterConfig {
  // Tipos de eventos que NO necesitan replay
  ignoreEventTypes: string[];

  // Errores específicos por mensaje
  ignoreErrorMessages: string[];

  // Performance issues a ignorar
  ignorePerformanceIssues: string[];

  // Niveles de severidad a ignorar
  ignoreLevels: string[];

  // URLs/rutas donde NO grabar replays
  ignoreRoutes: string[];
}

export const REPLAY_FILTER_CONFIG: ReplayFilterConfig = {
  // Tipos de eventos Sentry a ignorar
  ignoreEventTypes: [
    "transaction", // Eventos de performance/tracing
  ],

  // Mensajes de error comunes que no necesitan replay
  ignoreErrorMessages: [
    "ChunkLoadError",
    "Loading chunk",
    "Network Error",
    "Failed to fetch",
    "Script error",
    "ResizeObserver loop",
    "Non-Error promise rejection",
    "cancelled", 
  ],

  ignorePerformanceIssues: [
    "Large Render Blocking Asset",
    "Uncompressed Asset",
    "HTTP/1.1 Request",
  ],

  // Niveles de log que no necesitan replay
  ignoreLevels: [
    "debug",
    "info",
    "log",
    // Mantén 'warning', 'error', 'fatal' para grabar replays
  ],

  // Rutas donde no grabar replays (ej: páginas públicas sin autenticación)
  ignoreRoutes: [ "/public", "/health-check"],
};

/**
 * Verifica si un evento debe ser ignorado para replay
 */
export function shouldIgnoreReplay(event: any): boolean {
  // 1. Verificar tipo de evento
  if (
    event.type &&
    REPLAY_FILTER_CONFIG.ignoreEventTypes.includes(event.type)
  ) {
    console.debug("[Sentry] Replay ignorado - tipo de evento:", event.type);
    return true;
  }

  // 2. Verificar nivel de severidad
  if (event.level && REPLAY_FILTER_CONFIG.ignoreLevels.includes(event.level)) {
    console.debug("[Sentry] Replay ignorado - nivel:", event.level);
    return true;
  }

  // 3. Verificar performance issues
  const performanceIssue =
    event.contexts?.trace?.op || event.tags?.transaction || event.transaction;

  if (performanceIssue) {
    const isIgnoredPerf = REPLAY_FILTER_CONFIG.ignorePerformanceIssues.some(
      (issue) =>
        performanceIssue.includes(issue) ||
        event.message?.includes(issue) ||
        event.tags?.["performance.issue"]?.includes(issue)
    );

    if (isIgnoredPerf) {
      console.debug(
        "[Sentry] Replay ignorado - performance issue:",
        performanceIssue
      );
      return true;
    }
  }

  // 4. Verificar mensajes de error
  const errorMessage =
    event.exception?.values?.[0]?.value || event.message || "";

  if (errorMessage) {
    const isIgnoredError = REPLAY_FILTER_CONFIG.ignoreErrorMessages.some(
      (ignored) => errorMessage.toLowerCase().includes(ignored.toLowerCase())
    );

    if (isIgnoredError) {
      console.debug(
        "[Sentry] Replay ignorado - mensaje de error:",
        errorMessage
      );
      return true;
    }
  }

  // 5. Verificar ruta actual
  const currentPath = window?.location?.pathname || "";
  const isIgnoredRoute = REPLAY_FILTER_CONFIG.ignoreRoutes.some((route) =>
    currentPath.startsWith(route)
  );

  if (isIgnoredRoute) {
    console.debug("[Sentry] Replay ignorado - ruta:", currentPath);
    return true;
  }

  return false;
}
